import React, { useEffect, useRef, useState } from "react";
import {
  getLocalSystemDate,
  parseIsoDateToMDYUS,
  toIsoDate,
} from "../../../utils/dateUtils";
import { mergeFormDataWithStatusExtras } from "../../../utils/applicationFormPayload";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { getStandardizedSignatures } from "../../../utils/formHelpers";
import { routeSheetSchema } from "./general-forms.validation";
import { isGeneralFormVisible } from "./generalFormsCatalog";
import EvaluationInput from "../../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import EvaluationFormHeader from "../../EvaluationNew/components/EvaluationFormHeader";
import { useApplicationFormLogic } from "../../../hooks/useApplicationFormLogic";
import { request } from "../../../utils/api";
import CloseButton from "../../../components/Application/CloseButton";
import GeneralFormFooter from "./components/GeneralFormFooter";
import EntryPatientSignature from "./components/EntryPatientSignature";
import PropTypes from "prop-types";
import { DeleteIcon, PlusIcon, UserIcon, MedicalNotesIcon } from "../../../Icons";
import { PATIENT_THEME } from "../../Patients/patientTheme";
import {
  Section,
  Field,
  labelClass,
  fieldClass,
} from "../../Patients/components/clinicalForms/formLayout";

const emptyEntry = (entryNumber = 1) => ({
  entryNumber,
  visitDate: parseIsoDateToMDYUS(getLocalSystemDate()),
  visitCode: "",
  timeIn: "",
  timeOut: "",
  mileage: 0,
  patientSignatureUrl: "",
  patientSignatureImage: "",
  patientSignatureS3Key: "",
  patientSignatureS3Url: "",
  clearPatientSignature: false,
});

const ROUTE_SHEET_VISIT_CODES = [
  { group: "Billable", codes: ["E", "RV", "IV", "RC", "RE", "DC"] },
  { group: "Non-Billable", codes: ["Ref", "NF", "S"] },
  { group: "Office", codes: ["O", "IS"] },
];

const mapEntriesFromApi = (entries = []) =>
  entries.map((entry, index) => ({
    entryNumber: entry.entryNumber || index + 1,
    visitDate: parseIsoDateToMDYUS(entry.visitDate),
    visitCode: String(entry.visitCode ?? "").trim(),
    timeIn: entry.timeIn ?? "",
    timeOut: entry.timeOut ?? "",
    mileage:
      entry.mileage === "" || entry.mileage == null
        ? 0
        : Number(entry.mileage),
    patientSignatureUrl:
      entry.patientSignatureUrl || entry.patientSignature || "",
    patientSignatureImage: "",
    patientSignatureS3Key: entry.patientSignatureS3Key || "",
    patientSignatureS3Url: entry.patientSignatureS3Url || "",
    clearPatientSignature: false,
  }));

/** Strip transient canvas fields / keep S3 refs for backend recreate. */
const serializeEntriesForApi = (entries = []) =>
  entries.map((entry, index) => {
    const payload = {
      entryNumber: index + 1,
      visitDate: toIsoDate(entry.visitDate),
      visitCode: entry.visitCode,
      timeIn: entry.timeIn || undefined,
      timeOut: entry.timeOut || undefined,
      mileage:
        entry.mileage === "" || entry.mileage == null
          ? undefined
          : Number(entry.mileage),
    };

    if (entry.clearPatientSignature) {
      payload.clearPatientSignature = true;
    } else if (entry.patientSignatureImage) {
      payload.patientSignatureImage = entry.patientSignatureImage;
    } else if (entry.patientSignatureS3Key || entry.patientSignatureS3Url) {
      payload.patientSignatureS3Key = entry.patientSignatureS3Key || null;
      payload.patientSignatureS3Url = entry.patientSignatureS3Url || null;
    }

    return payload;
  });

const RouteSheet = ({ onClose }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [routeSheetId, setRouteSheetId] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormLoaded, setIsFormLoaded] = useState(!isEditMode);
  const submitLockRef = useRef(false);

  const methods = useForm({
    resolver: zodResolver(routeSheetSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      employeeName: "",
      applicantName: "",
      title: "",
      patientName: "",
      mrNumber: "",
      applicantSignature: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      entries: [emptyEntry(1)],
    },
    mode: "onChange",
  });

  const { handleSubmit, watch, control, reset, setValue } = methods;

  const onSubmit = async (data, flag = "completed", additionalData = {}) => {
    if (submitLockRef.current || isSubmitting) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const actualFlag = typeof flag === "string" ? flag : "completed";
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const fullName =
        merged.employeeName || merged.applicantName || userFullName;
      const payload = {
        ...merged,
        employeeName: fullName,
        applicantName: fullName,
        applicantSignedAt: toIsoDate(merged.applicantSignedAt),
        reviewedBySignedAt: toIsoDate(merged.reviewedBySignedAt),
        entries: serializeEntriesForApi(merged.entries || []),
        flag: actualFlag,
      };
      if (currentStatus === "completed") {
        payload.flag = "completed";
      }
      const finalComments =
        additionalData?.comments ||
        data?.comments ||
        methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }
      const effectiveId = id || routeSheetId;
      const url = effectiveId ? `/route-sheets/${effectiveId}` : "/route-sheets";
      const method = effectiveId ? "PUT" : "POST";

      const response = await request(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success(
          response.message || "Route Sheet processed successfully!",
        );
        navigate(
          isGeneralFormVisible("route-sheet")
            ? "/general-forms/list?tab=route-sheet"
            : "/general-forms/list",
        );
      } else {
        toast.error(response.message || "Failed to process form!");
        submitLockRef.current = false;
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const {
    targetUserId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    signerName,
    handleSignatureApply,
    handleStatusAction,
    handleSave,
    user: currentUserData,
    isAdmin,
    isReadOnly: hookReadOnly,
    closeForm,
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus,
  });

  // Admin: full edit on General Forms (fill, sign, save, complete) for any employee form.
  const isReadOnly = isAdmin ? false : hookReadOnly;
  const sigDisabled = Boolean(isReadOnly);

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "entries",
  });

  const userFullName = currentUserData
    ? `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim() ||
      currentUserData.name ||
      ""
    : "";

  useEffect(() => {
    const fetchRouteSheet = async () => {
      if (!isEditMode) return;
      try {
        const response = await request(`/route-sheets/${id}`);
        if (response.status === 1 && response.result?.routeSheet) {
          const data = response.result.routeSheet;
          const mappedEntries = mapEntriesFromApi(
            data.entries || [emptyEntry(1)],
          );
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName:
              data.employeeName || data.applicantName || userFullName,
            applicantName:
              data.applicantName || data.employeeName || userFullName,
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(
              data.applicantSignedAt || data.applicantSignatureDate,
            ),
            reviewedBySignedAt: parseIsoDateToMDYUS(
              data.reviewedBySignedAt ||
                data.reviewedBySignatureDate ||
                data.adminSignatureDate,
            ),
            entries: mappedEntries,
          });
          replace(mappedEntries);
          mappedEntries.forEach((entry, entryIndex) => {
            if (entry.visitCode) {
              setValue(`entries.${entryIndex}.visitCode`, entry.visitCode, {
                shouldDirty: false,
                shouldValidate: false,
              });
            }
          });
          setIsFormLoaded(true);
        } else {
          toast.error("Failed to load route sheet");
          setIsFormLoaded(true);
        }
      } catch (error) {
        console.error("Error loading route sheet:", error);
        toast.error("Error loading route sheet");
        setIsFormLoaded(true);
      }
    };

    const fetchByUserId = async () => {
      if (!targetUserId || !currentUserData) return;
      try {
        const response = await request(`/route-sheets?userId=${targetUserId}`);
        if (response.status === 1 && response.result?.items?.length > 0) {
          const data = response.result.items[0];
          const mappedEntries = mapEntriesFromApi(
            data.entries || [emptyEntry(1)],
          );
          setRouteSheetId(data.id);
          setCurrentStatus(data.status || "");
          reset({
            ...data,
            employeeName:
              data.employeeName || data.applicantName || userFullName,
            applicantName:
              data.applicantName || data.employeeName || userFullName,
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(
              data.applicantSignedAt || data.applicantSignatureDate,
            ),
            reviewedBySignedAt: parseIsoDateToMDYUS(
              data.reviewedBySignedAt ||
                data.reviewedBySignatureDate ||
                data.adminSignatureDate,
            ),
            entries: mappedEntries,
          });
          replace(mappedEntries);
          mappedEntries.forEach((entry, entryIndex) => {
            if (entry.visitCode) {
              setValue(`entries.${entryIndex}.visitCode`, entry.visitCode, {
                shouldDirty: false,
                shouldValidate: false,
              });
            }
          });
          setIsFormLoaded(true);
        } else {
          setIsFormLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching route sheet by userId:", error);
        setIsFormLoaded(true);
      }
    };

    if (isEditMode) {
      fetchRouteSheet();
    } else {
      fetchByUserId();
    }
  }, [isEditMode, id, targetUserId, reset, replace, setValue, currentUserData, isAdmin, userFullName]);

  const onCloseFrom = () => {
    navigate("/general-forms/list");
  };

  if (!isGeneralFormVisible("route-sheet")) {
    return <Navigate to="/general-forms" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-100/80 py-8 px-3 sm:px-6 lg:px-8 font-sans">
      <CloseButton onClose={onCloseFrom} />

      <div
        className={`mx-auto w-full max-w-5xl bg-white rounded-xl border shadow-sm overflow-hidden relative ${PATIENT_THEME.section.wrap}`}
      >
        <EvaluationFormHeader title="ROUTE SHEET" />

        <div className="border-none p-0 m-0">
          <div className="p-4 sm:p-6 md:p-8 space-y-3">
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit((data) => onSubmit(data))}
                className="space-y-3"
              >
                <Section icon={UserIcon} title="Employee & Patient">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Employee Name">
                      <EvaluationInput
                        name="employeeName"
                        className="w-full"
                        inputClassName={fieldClass}
                        disabled={sigDisabled}
                      />
                    </Field>
                    <Field label="Title">
                      <EvaluationInput
                        name="title"
                        className="w-full"
                        inputClassName={fieldClass}
                        disabled={sigDisabled}
                      />
                    </Field>
                    <Field label="Patient Name">
                      <EvaluationInput
                        name="patientName"
                        className="w-full"
                        inputClassName={fieldClass}
                        disabled={sigDisabled}
                      />
                    </Field>
                    <Field label="MR Number">
                      <EvaluationInput
                        name="mrNumber"
                        className="w-full"
                        inputClassName={fieldClass}
                        disabled={sigDisabled}
                      />
                    </Field>
                  </div>
                </Section>

                <Section icon={MedicalNotesIcon} title="Visit Entries">
                  <div className="space-y-3">
                    {!isFormLoaded ? (
                      <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-6 text-center text-xs text-slate-500">
                        Loading visit entries…
                      </div>
                    ) : null}
                    {isFormLoaded &&
                      fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="rounded-md border border-slate-200 bg-white overflow-hidden"
                      >
                        <div
                          className={`flex items-center justify-between gap-2 px-3 py-1.5 border-b ${PATIENT_THEME.section.head}`}
                        >
                          <span
                            className={`text-[10px] font-semibold uppercase tracking-wide ${PATIENT_THEME.section.title}`}
                          >
                            Entry {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-40"
                            disabled={fields.length === 1 || sigDisabled}
                            title="Remove entry"
                            aria-label={`Remove entry ${index + 1}`}
                          >
                            <DeleteIcon size={14} />
                          </button>
                        </div>

                        <div className="p-3 space-y-3">
                          <input
                            type="hidden"
                            {...methods.register(
                              `entries.${index}.entryNumber`,
                            )}
                            value={index + 1}
                          />

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
                            <div>
                              <label className={labelClass}>Date</label>
                              <InputDatePicker
                                formatSeparator="/"
                                name={`entries.${index}.visitDate`}
                                inputClassName={fieldClass}
                                disabled={sigDisabled}
                              />
                            </div>
                            <div>
                              <label className={labelClass}>Code</label>
                              <select
                                className={fieldClass}
                                value={
                                  watch(`entries.${index}.visitCode`) ?? ""
                                }
                                disabled={sigDisabled}
                                onChange={(event) =>
                                  setValue(
                                    `entries.${index}.visitCode`,
                                    event.target.value,
                                    { shouldDirty: true, shouldValidate: true },
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {ROUTE_SHEET_VISIT_CODES.map(
                                  ({ group, codes }) => (
                                    <optgroup key={group} label={group}>
                                      {codes.map((code) => (
                                        <option key={code} value={code}>
                                          {code}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ),
                                )}
                              </select>
                            </div>
                            <div>
                              <label className={labelClass}>Time In</label>
                              <EvaluationInput
                                name={`entries.${index}.timeIn`}
                                type="time"
                                inputClassName={fieldClass}
                                disabled={sigDisabled}
                              />
                            </div>
                            <div>
                              <label className={labelClass}>Time Out</label>
                              <EvaluationInput
                                name={`entries.${index}.timeOut`}
                                type="time"
                                inputClassName={fieldClass}
                                disabled={sigDisabled}
                              />
                            </div>
                            <div>
                              <label className={labelClass}>Mileage</label>
                              <EvaluationInput
                                name={`entries.${index}.mileage`}
                                type="number"
                                placeholder="0"
                                inputClassName={fieldClass}
                                disabled={sigDisabled}
                              />
                            </div>
                          </div>

                          <div>
                            <label className={labelClass}>
                              Patient Signature
                            </label>
                            <EntryPatientSignature
                              index={index}
                              disabled={sigDisabled}
                              height={96}
                            />
                          </div>
                        </div>
                      </div>
                      ))}

                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        disabled={fields.length >= 10 || sigDisabled}
                        onClick={() => append(emptyEntry(fields.length + 1))}
                        className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border transition-colors disabled:opacity-50 ${PATIENT_THEME.button.soft}`}
                      >
                        <PlusIcon size={14} />
                        Add Entry
                      </button>
                    </div>
                  </div>
                </Section>

                <GeneralFormFooter
                  methods={methods}
                  onSave={handleSave}
                  onSubmit={(data, status) => onSubmit(data, status)}
                  onStatusUpdate={handleStatusAction}
                  isSignatureModalOpen={isSignatureModalOpen}
                  openSignatureModal={openSignatureModal}
                  closeSignatureModal={closeSignatureModal}
                  handleSignatureApply={handleSignatureApply}
                  signerName={signerName}
                  employeeName={watch("employeeName") || ""}
                  applicantSignature={watch("applicantSignature") || ""}
                  applicantSignedAt={watch("applicantSignedAt") || ""}
                  reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                  reviewedBySignature={watch("reviewedBySignature") || ""}
                  reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                  isReadOnly={isReadOnly || isSubmitting}
                  isSubmitting={isSubmitting}
                  currentStatus={currentStatus}
                  submitLabel="Submit Route Sheet"
                  onClose={closeForm}
                />
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

RouteSheet.propTypes = {
  onClose: PropTypes.func,
};

export default RouteSheet;
